import React, { useState, useMemo, useRef, useEffect } from 'react';
import { analysisApi } from '../lib/api';
import { samplePoints } from '../lib/utils';
import { run, isAbortError } from '../lib/worker-pool';
import { COLOR_RAMPS } from '../lib/classify';
import { useTheme } from '../contexts/ThemeContext';
import { useFeature } from '../lib/runtimeConfig';
import { useOrg } from '../contexts/OrgContext';
import { trackJob, fetchJobResult, gridResultToGeoJSON } from '../lib/jobs-api';
import { buildBreaklineFields, buildPreprocessFields, summarizePreprocess } from '../lib/gridding-options';
import { estimateGridCells, assertGridSize, layerExtent, unitAbbr, MAX_GRID_CELLS } from '../lib/geoprocess-meta';

const HOTSPOT_METHODS = [
  { id: 'lisa', label: 'Local Moran (LISA)' },
  { id: 'getis', label: 'Getis-Ord Gi*' },
];

/** Extent of a Square / Hex Grid run: the chosen extent layer, else the current map view. */
export function gridExtentFor(params, layers, getViewExtent) {
  if (params.extentLayer) return layerExtent(layers.find(l => l.id === params.extentLayer)?.geojson);
  return getViewExtent ? getViewExtent() : null;
}

/** "sampled 20k of 50k" for a layer whose input was thinned to the backend cap (V.50). */
export function sampledLabel(nUsed, nIn) {
  const short = (n) => (n >= 1000 && n % 1000 === 0 ? `${n / 1000}k` : n.toLocaleString('en-US'));
  return `sampled ${short(nUsed)} of ${short(nIn)}`;
}

// Gridding point caps: larger inputs are queued as background jobs when the `jobs` feature is on.
const SLOW_GRIDDING = ['kriging', 'rbf-multiquadric', 'rbf-thin-plate'];
const griddingPointCap = (method, jobsEnabled) =>
  (jobsEnabled ? (SLOW_GRIDDING.includes(method) ? 3000 : 20000) : 5000);

const TOOLS = [
  { id: 'buffer', label: 'Buffer', icon: '⭕', category: 'Geometry', desc: 'Zones around features' },
  { id: 'centroid', label: 'Centroids', icon: '⊕', category: 'Geometry', desc: 'Geometric centers' },
  { id: 'convex', label: 'Convex Hull', icon: '⬡', category: 'Geometry', desc: 'Smallest enclosing polygon' },
  { id: 'dissolve', label: 'Dissolve', icon: '∪', category: 'Geometry', desc: 'Merge all polygons' },
  { id: 'union', label: 'Union', icon: '⋃', category: 'Overlay', desc: 'Combine two layers' },
  { id: 'intersect', label: 'Intersect', icon: '⋂', category: 'Overlay', desc: 'Common area of two layers' },
  { id: 'clip', label: 'Clip', icon: '✂', category: 'Overlay', desc: 'Cut features to mask' },
  { id: 'merge', label: 'Merge Layers', icon: '⊕', category: 'Overlay', desc: 'Combine into one layer' },
  { id: 'grid-square', label: 'Square Grid', icon: '▦', category: 'Generate', desc: 'Regular square grid' },
  { id: 'grid-hex', label: 'Hex Grid', icon: '⬡', category: 'Generate', desc: 'Hexagonal grid' },
  { id: 'dedup', label: 'Remove Duplicates', icon: '🧹', category: 'Cleaning', desc: 'Drop repeated features' },
  { id: 'gridding', label: 'Gridding (Interpolation)', icon: '🌐', category: 'Geostatistics', desc: 'IDW, Kriging, RBF, Min Curvature, Natural Neighbor' },
  { id: 'contour', label: 'Contour Map', icon: '〰️', category: 'Geostatistics', desc: 'Isolines + filled bands from a gridding result' },
  { id: 'lisa', label: 'Hot Spot', icon: '🔥', category: 'Statistics', desc: 'Local Moran (LISA) clusters or Getis-Ord Gi* hot/cold spots' },
  { id: 'morans', label: "Moran's I", icon: 'Σ', category: 'Statistics', desc: 'Global spatial autocorrelation test' },
];

const CATEGORIES = ['All', 'Geometry', 'Overlay', 'Generate', 'Cleaning', 'Geostatistics', 'Statistics'];

const BACKEND_TOOLS = ['gridding', 'lisa', 'morans'];

const GRIDDING_METHODS = [
  { id: 'idw', label: 'IDW (Inverse Distance)' },
  { id: 'kriging', label: 'Kriging (Ordinary)' },
  { id: 'rbf-multiquadric', label: 'RBF — Multiquadric' },
  { id: 'rbf-thin-plate', label: 'RBF — Thin Plate Spline' },
  { id: 'min-curvature', label: 'Minimum Curvature' },
  { id: 'natural-neighbor', label: 'Natural Neighbor' },
];

/** Extract lon/lat/value points from a layer's Point features (skips excluded rows). */
function extractPoints(geojson, valueAttr) {
  return (geojson?.features || [])
    .filter(f => f.geometry?.type === 'Point' && !f.properties?._excluded)
    .map((f, i) => ({
      lon: f.geometry.coordinates[0],
      lat: f.geometry.coordinates[1],
      value: parseFloat(f.properties?.[valueAttr]),
      label: String(f.properties?.name ?? f.properties?.label ?? `P${i}`),
    }))
    .filter(p => !isNaN(p.value) && isFinite(p.value));
}

/**
 * AnalysisPanel — geoprocessing toolkit sidebar
 * 
 * Props:
 *   layers - array of { id, name, geojson } available layers
 *   onResult - callback(resultGeoJSON, resultName) to add result layer to map
 *   onClose - close panel
 *   onDrawLine - optional; start drawing a polyline on the map (breaklines)
 */
export default function AnalysisPanel({ layers = [], onResult, onClose, onDrawLine, initialTool = null, initialParams = null, getViewExtent = null }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [category, setCategory] = useState('All');
  const [activeTool, setActiveTool] = useState(initialTool);
  const [params, setParams] = useState(initialParams || {});
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);
  const [progress, setProgress] = useState(0);
  const aliveRef = useRef(true);   // false after unmount: skip state updates from awaited work
  const abortRef = useRef(null);   // AbortController of the in-flight worker run

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const addLog = (msg, type = 'info') => {
    if (!aliveRef.current) return;
    setLog(prev => [...prev.slice(-20), { msg, type, time: new Date().toLocaleTimeString() }]);
  };

  // Hide backend-computed tools whose feature is disabled on this deployment.
  const analysisEnabled = useFeature('analysis');
  const spatialStatsEnabled = useFeature('spatial_stats');
  const jobsEnabled = useFeature('jobs');
  const maxGridRes = jobsEnabled ? 500 : 200;
  const { selectedOrgId } = useOrg();
  const availableTools = TOOLS.filter(t =>
    (t.id !== 'gridding' || analysisEnabled) &&
    (!['lisa', 'morans'].includes(t.id) || spatialStatsEnabled));
  const filteredTools = category === 'All' ? availableTools : availableTools.filter(t => t.category === category);

  // One option per layer id: a layer listed twice would render duplicate options (V.31)
  const uniqueLayers = useMemo(() => {
    const seen = new Set();
    return layers.filter(l => (seen.has(l.id) ? false : (seen.add(l.id), true)));
  }, [layers]);

  // Numeric attributes of the selected source layer's point features
  const srcNumericAttrs = useMemo(() => {
    const src = layers.find(l => l.id === params.sourceLayer);
    if (!src?.geojson?.features) return [];
    const counts = {};
    src.geojson.features.slice(0, 300).forEach(f => {
      if (f.geometry?.type !== 'Point') return;
      Object.entries(f.properties || {}).forEach(([k, v]) => {
        const num = parseFloat(v);
        if (!isNaN(num) && isFinite(num)) counts[k] = (counts[k] || 0) + 1;
      });
    });
    return Object.keys(counts);
  }, [layers, params.sourceLayer]);

  const runTool = async () => {
    setRunning(true);
    setError(null);
    try {
      const srcLayer = layers.find(l => l.id === params.sourceLayer);
      const isGridTool = activeTool === 'grid-square' || activeTool === 'grid-hex';
      if (!srcLayer && !isGridTool) {
        throw new Error('Select a source layer');
      }
      const srcGeoJSON = srcLayer?.geojson;
      let result = null;
      let name = '';
      let kind = 'vector';

      // Backend geostatistics / spatial statistics tools
      if (BACKEND_TOOLS.includes(activeTool)) {
        if (!params.valueAttr) throw new Error('Select a value attribute');
        const allPoints = extractPoints(srcGeoJSON, params.valueAttr);
        if (allPoints.length < 5) throw new Error(`Need ≥5 point features with numeric "${params.valueAttr}" (found ${allPoints.length})`);
        // Backend caps: gridding 5000 pts (20000 / 3000 for kriging+RBF when large grids
        // may be queued as background jobs), 2000 for spatial stats (O(n²) weights)
        const cap = activeTool === 'gridding' ? griddingPointCap(params.method || 'idw', jobsEnabled) : 2000;
        const points = samplePoints(allPoints, cap);
        const sampled = points.length < allPoints.length;
        if (sampled) addLog(`Sampled ${points.length} of ${allPoints.length} points (backend limit)`, 'info');

        if (activeTool === 'gridding') {
          const method = params.method || 'idw';
          name = `${GRIDDING_METHODS.find(m => m.id === method)?.label || method}: ${params.valueAttr} — ${srcLayer.name}`;
          const body = {
            points,
            method,
            grid_resolution: Math.min(Math.max(parseInt(params.gridRes) || 50, 10), maxGridRes),
            variogram_model: params.variogramModel || 'spherical',
            power: parseFloat(params.power) || 2.0,
            ...buildPreprocessFields(params),
            ...buildBreaklineFields(params, layers.find(l => l.id === 'drawn')?.geojson?.features || []),
          };
          if (selectedOrgId) body.organization_id = selectedOrgId;
          const res = await analysisApi.gridding(body);
          if (res.status === 202 && res.data?.job) {
            addLog('Queued as background job', 'info');
            const job = await trackJob(res.data.job, { label: name });
            if (job.status === 'succeeded') {
              result = gridResultToGeoJSON(await fetchJobResult(job.id));
            } else {
              const msg = job.status === 'cancelled' ? 'Job cancelled' : (job.error || 'Job failed');
              if (job.status === 'cancelled') addLog(msg, 'info');
              else { if (aliveRef.current) setError(msg); addLog(`✗ ${msg}`, 'error'); }
              if (aliveRef.current) setRunning(false);
              return;
            }
          } else {
            result = res.data;
          }
          kind = 'grid';
          const ppSummary = summarizePreprocess(result?.metadata?.preprocess);
          if (ppSummary) addLog(`Pre-processing: ${ppSummary}`, 'info');
          if (result?.metadata?.preprocess?.breaklines) addLog(`${result.metadata.preprocess.breaklines} breakline segment(s) honoured`, 'info');
        } else if (activeTool === 'lisa' && params.hotspotMethod === 'getis') {
          const res = await analysisApi.getisOrd({ points, k: parseInt(params.k) || 8 });
          result = res.data;
          kind = 'categorical';
          name = `Getis-Ord Gi* ${params.valueAttr} — ${srcLayer.name}`;
          const cnt = {};
          (result?.features || []).forEach(f => { const c = f.properties?.category || 'ns'; cnt[c] = (cnt[c] || 0) + 1; });
          addLog(`Hot 99%:${cnt['hot-99'] || 0} 95%:${cnt['hot-95'] || 0} · Cold 99%:${cnt['cold-99'] || 0} 95%:${cnt['cold-95'] || 0} · NS:${cnt.ns || 0}`, 'info');
        } else if (activeTool === 'lisa') {
          const res = await analysisApi.lisa({
            points,
            k: parseInt(params.k) || 8,
            permutations: 199,
          });
          result = res.data;
          kind = 'categorical';
          const cc = res.data.metadata?.cluster_counts || {};
          name = `LISA ${params.valueAttr} — ${srcLayer.name}`;
          addLog(`Clusters — HH:${cc.HH ?? 0} LL:${cc.LL ?? 0} HL:${cc.HL ?? 0} LH:${cc.LH ?? 0} NS:${cc.NS ?? 0}`, 'info');
        } else if (activeTool === 'morans') {
          const res = await analysisApi.moransI({
            points,
            k: parseInt(params.k) || 8,
            permutations: 999,
          });
          const d = res.data;
          addLog(`Moran's I = ${d.morans_i} (expected ${d.expected_i})`, 'success');
          addLog(`z = ${d.z_score}, p = ${d.p_value} → ${d.interpretation}`, d.p_value < 0.05 ? 'success' : 'info');
          setRunning(false);
          return; // stats only — no result layer
        }

        if (result && onResult) {
          if (sampled) {
            // V.50: label the layer so nobody mistakes a thinned input for the full dataset.
            name = `${name} (${sampledLabel(points.length, allPoints.length)})`;
            result = { ...result, metadata: { ...(result.metadata || {}), sampled: true, n_in: allPoints.length, n_used: points.length } };
          }
          onResult(result, name, kind);
          addLog(`✓ ${name} — ${result.features.length} features`, 'success');
        }
        setRunning(false);
        return;
      }

      // Vector tools run in the worker pool (lib/worker-pool.js, task 'analysis') so large
      // layers do not freeze the UI; progress and Cancel show up in TaskConsole.
      const toolLabel = TOOLS.find(t => t.id === activeTool)?.label || activeTool;
      const needsTargetLayer = ['union', 'intersect', 'clip', 'merge'].includes(activeTool);
      const tgtLayer = needsTargetLayer ? layers.find(l => l.id === params.targetLayer) : null;
      if (needsTargetLayer && !tgtLayer) {
        throw new Error(activeTool === 'clip' ? 'Select a mask layer' : activeTool === 'merge' ? 'Select a second layer' : 'Select a target layer');
      }
      let runParams = params;
      if (isGridTool) {
        // Square / Hex Grid: map view (or the chosen extent layer), refused above MAX_GRID_CELLS.
        const extent = gridExtentFor(params, layers, getViewExtent);
        const cells = assertGridSize(extent, parseFloat(params.gridSize) || 10, params.gridUnit || 'kilometers', activeTool === 'grid-hex' ? 'hex' : 'square');
        addLog(`Building about ${cells.toLocaleString()} cells`, 'info');
        runParams = { ...params, extent };
      }
      const ctl = new AbortController();
      abortRef.current = ctl;
      setProgress(0);
      const out = await run(
        'analysis',
        { tool: activeTool, src: isGridTool ? null : srcGeoJSON, target: tgtLayer?.geojson, params: runParams },
        {
          label: `${toolLabel}${srcLayer ? ` — ${srcLayer.name}` : ''}`,
          signal: ctl.signal,
          onProgress: (v) => { if (aliveRef.current) setProgress(v); },
        },
      );
      if (!aliveRef.current) return; // panel closed while the worker was running
      result = out.result;
      kind = out.kind;
      switch (activeTool) {
        case 'contour': name = `Contours (${out.intervals}) — ${srcLayer.name}`; break;
        case 'buffer': {
          const radius = parseFloat(params.bufferRadius) || 1;
          const unit = params.bufferUnit || 'kilometers';
          name = `Buffer ${radius} ${unitAbbr(unit)} — ${srcLayer.name}`;
          break;
        }
        case 'centroid': name = `Centroids — ${srcLayer.name}`; break;
        case 'convex': name = `Convex Hull — ${srcLayer.name}`; break;
        case 'dissolve': name = `Dissolved — ${srcLayer.name}`; break;
        case 'union': name = `Union — ${srcLayer.name} + ${tgtLayer.name}`; break;
        case 'intersect': name = `Intersect — ${srcLayer.name} ∩ ${tgtLayer.name}`; break;
        case 'clip': name = `Clipped — ${srcLayer.name}`; break;
        case 'merge': name = `Merged — ${srcLayer.name} + ${tgtLayer.name}`; break;
        case 'grid-square':
        case 'grid-hex': {
          const cellSize = parseFloat(params.gridSize) || 10;
          const unit = params.gridUnit || 'kilometers';
          name = `${activeTool === 'grid-hex' ? 'Hex' : 'Square'} Grid ${cellSize} ${unitAbbr(unit)}`;
          break;
        }
        case 'dedup': name = `Deduped — ${srcLayer.name} (${out.removed} removed)`; break;
        default: break;
      }

      if (result && !(result.features?.length)) {
        // An empty result is not a success: say so and do not add an empty layer.
        const why = activeTool === 'clip' ? ' (no features fall inside the mask polygons)' : '';
        addLog(`⚠ ${toolLabel} produced 0 features${why}; nothing added`, 'warning');
      } else if (result && onResult) {
        onResult(result, name, kind);
        addLog(`✓ ${name} — ${result.features.length} features`, 'success');
      }
    } catch (err) {
      if (!aliveRef.current) return;
      if (isAbortError(err)) {
        addLog('Cancelled', 'info');
      } else {
        const msg = err.response?.data?.detail || err.message;
        setError(msg);
        addLog(`✗ ${msg}`, 'error');
      }
    } finally {
      abortRef.current = null;
      if (aliveRef.current) { setRunning(false); setProgress(0); }
    }
  };

  const needsTarget = ['union', 'intersect', 'clip', 'merge'].includes(activeTool);
  const needsBuffer = activeTool === 'buffer';
  const needsGrid = ['grid-square', 'grid-hex'].includes(activeTool);
  const needsValueAttr = BACKEND_TOOLS.includes(activeTool);
  const needsGridRes = activeTool === 'gridding';
  const needsK = ['lisa', 'morans'].includes(activeTool);
  const needsContour = activeTool === 'contour';
  const griddingMethod = params.method || 'idw';

  return (
    <div style={{ width: 280, background: C.bg2, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
      {/* Header */}
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>Analysis Tools</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCategory(c)}
            style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: 'none', background: category === c ? C.bg4 : 'transparent', color: category === c ? C.cyan : C.text3, cursor: 'pointer' }}>
            {c}
          </button>
        ))}
      </div>

      {/* Tool list */}
      <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
        {filteredTools.map(tool => (
          <div key={tool.id} onClick={() => { setActiveTool(tool.id); setError(null); setParams({}); }}
            style={{ padding: '8px 10px', marginBottom: 4, borderRadius: 8, background: activeTool === tool.id ? C.bg4 : C.bg3, border: `1px solid ${activeTool === tool.id ? C.cyan + '44' : C.border}`, cursor: 'pointer', transition: 'all .15s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>{tool.icon}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>{tool.label}</div>
                <div style={{ fontSize: 10, color: C.text2 }}>{tool.desc}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Parameters */}
      {activeTool && (
        <div style={{ padding: '10px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: C.text2, fontWeight: 600, textTransform: 'uppercase' }}>Parameters</div>

          {/* Source layer */}
          {activeTool !== 'grid-square' && activeTool !== 'grid-hex' && (
            <div>
              <label style={{ fontSize: 10, color: C.text2 }}>Source Layer</label>
              <select value={params.sourceLayer || ''} onChange={e => setParams(p => ({ ...p, sourceLayer: e.target.value }))}
                style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                <option value="">Select layer...</option>
                {uniqueLayers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}

          {/* Target layer for overlay ops */}
          {needsTarget && (
            <div>
              <label style={{ fontSize: 10, color: C.text2 }}>{activeTool === 'clip' ? 'Mask Layer' : 'Target Layer'}</label>
              <select value={params.targetLayer || ''} onChange={e => setParams(p => ({ ...p, targetLayer: e.target.value }))}
                style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                <option value="">Select layer...</option>
                {uniqueLayers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}

          {/* Buffer params */}
          {needsBuffer && (
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 10, color: C.text2 }}>Radius</label>
                <input type="number" value={params.bufferRadius || ''} onChange={e => setParams(p => ({ ...p, bufferRadius: e.target.value }))}
                  placeholder="1" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: C.text2 }}>Unit</label>
                <select value={params.bufferUnit || 'kilometers'} onChange={e => setParams(p => ({ ...p, bufferUnit: e.target.value }))}
                  style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                  <option value="meters">m</option>
                  <option value="kilometers">km</option>
                  <option value="miles">mi</option>
                </select>
              </div>
            </div>
          )}

          {/* Grid params */}
          {needsGrid && (
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 10, color: C.text2 }}>Cell Size</label>
                <input type="number" value={params.gridSize || ''} onChange={e => setParams(p => ({ ...p, gridSize: e.target.value }))}
                  placeholder="10" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: C.text2 }}>Unit</label>
                <select value={params.gridUnit || 'kilometers'} onChange={e => setParams(p => ({ ...p, gridUnit: e.target.value }))}
                  style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                  <option value="meters">m</option>
                  <option value="kilometers">km</option>
                </select>
              </div>
            </div>
          )}
          {needsGrid && (
            <div>
              <label style={{ fontSize: 10, color: C.text2 }}>Extent</label>
              <select value={params.extentLayer || ''} onChange={e => setParams(p => ({ ...p, extentLayer: e.target.value }))}
                style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                <option value="">Current map view</option>
                {uniqueLayers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              {(() => {
                const cells = estimateGridCells(gridExtentFor(params, layers, getViewExtent), parseFloat(params.gridSize) || 10, params.gridUnit || 'kilometers', activeTool === 'grid-hex' ? 'hex' : 'square');
                const over = !(cells <= MAX_GRID_CELLS);
                return (
                  <div data-testid="grid-estimate" style={{ fontSize: 10, marginTop: 4, color: over ? C.amber : C.text3 }}>
                    {Number.isFinite(cells)
                      ? `≈ ${cells.toLocaleString()} cells${over ? ` (over the ${MAX_GRID_CELLS.toLocaleString()} limit: larger cells or a smaller extent)` : ''}`
                      : 'No extent yet: zoom the map to the area or pick an extent layer'}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Geostatistics / statistics params */}
          {needsValueAttr && (
            <div>
              <label style={{ fontSize: 10, color: C.text2 }}>Value Attribute (numeric)</label>
              <select value={params.valueAttr || ''} onChange={e => setParams(p => ({ ...p, valueAttr: e.target.value }))}
                style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                <option value="">Select attribute...</option>
                {srcNumericAttrs.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          )}
          {needsGridRes && (
            <>
              <div>
                <label style={{ fontSize: 10, color: C.text2 }}>Gridding Method</label>
                <select value={griddingMethod} onChange={e => setParams(p => ({ ...p, method: e.target.value }))}
                  style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                  {GRIDDING_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: C.text2 }}>Grid Resolution</label>
                  <input type="number" min={10} max={maxGridRes} value={params.gridRes || ''} onChange={e => setParams(p => ({ ...p, gridRes: e.target.value }))}
                    placeholder="50" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                {griddingMethod === 'idw' && (
                  <div style={{ width: 70 }}>
                    <label style={{ fontSize: 10, color: C.text2 }}>Power</label>
                    <input type="number" value={params.power || ''} onChange={e => setParams(p => ({ ...p, power: e.target.value }))}
                      placeholder="2" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                )}
                {griddingMethod === 'kriging' && (
                  <div>
                    <label style={{ fontSize: 10, color: C.text2 }}>Variogram</label>
                    <select value={params.variogramModel || 'spherical'} onChange={e => setParams(p => ({ ...p, variogramModel: e.target.value }))}
                      style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                      <option value="spherical">Spherical</option>
                      <option value="exponential">Exponential</option>
                      <option value="gaussian">Gaussian</option>
                    </select>
                  </div>
                )}
              </div>
              {/* Pre-processing (off by default; applied before interpolation) */}
              <details style={{ fontSize: 10, color: C.text2 }}>
                <summary style={{ cursor: 'pointer' }}>Pre-processing and breaklines</summary>
                {(() => {
                  const fld = { fontSize: 11, padding: '4px 6px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' };
                  const set = (k) => (e) => setParams(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={!!params.ppDedupe} onChange={set('ppDedupe')} /> Merge duplicate points
                      </label>
                      {params.ppDedupe && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input type="number" min="0" step="any" placeholder="tolerance (deg)" value={params.ppDedupeTol || ''} onChange={set('ppDedupeTol')} style={{ ...fld, flex: 1, minWidth: 0 }} />
                          <select value={params.ppDedupeStat || 'mean'} onChange={set('ppDedupeStat')} style={fld}>
                            <option value="mean">mean z</option>
                            <option value="median">median z</option>
                          </select>
                        </div>
                      )}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={!!params.ppOutlier} onChange={set('ppOutlier')} /> Remove outliers
                      </label>
                      {params.ppOutlier && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <select value={params.ppOutlierMethod || 'zscore'} onChange={set('ppOutlierMethod')} style={fld}>
                            <option value="zscore">z-score</option>
                            <option value="iqr">IQR</option>
                          </select>
                          <input type="number" min="0" step="any" placeholder={params.ppOutlierMethod === 'iqr' ? 'k (1.5)' : 'k (3)'} value={params.ppOutlierK || ''} onChange={set('ppOutlierK')} style={{ ...fld, flex: 1, minWidth: 0 }} />
                        </div>
                      )}
                      {(griddingMethod === 'idw' || griddingMethod === 'kriging') && (
                        <>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input type="checkbox" checked={!!params.ppDecluster} onChange={set('ppDecluster')} /> Decluster (cell weights)
                          </label>
                          {params.ppDecluster && (
                            <input type="number" min="0" step="any" placeholder="cell size (deg)" value={params.ppDeclusterCell || ''} onChange={set('ppDeclusterCell')} style={fld} />
                          )}
                        </>
                      )}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={!!params.ppBreaklines} onChange={set('ppBreaklines')} /> Breaklines (drawn lines)
                      </label>
                      {params.ppBreaklines && (
                        <div style={{ lineHeight: 1.5 }}>
                          {(() => {
                            const n = (layers.find(l => l.id === 'drawn')?.geojson?.features || [])
                              .filter(f => ['LineString', 'MultiLineString'].includes(f.geometry?.type)).length;
                            return (
                              <>
                                <span style={{ color: n ? C.emerald : C.amber }}>
                                  {n ? `✓ ${n} drawn line${n > 1 ? 's' : ''} act as breaklines` : 'Draw polylines on the map first'}
                                </span>
                                {onDrawLine && <button onClick={onDrawLine} style={{ ...fld, marginLeft: 6, cursor: 'pointer' }}>✏️ Draw line</button>}
                                <div>Points across a line are not blended; minimum curvature treats it as a fault.</div>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </details>
            </>
          )}
          {/* Contour params */}
          {needsContour && (
            <>
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: C.text2 }}>Intervals</label>
                  <input type="number" value={params.intervals || ''} onChange={e => setParams(p => ({ ...p, intervals: e.target.value }))}
                    placeholder="10" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: C.text2 }}>Color Ramp</label>
                  <select value={params.ramp || 'heat'} onChange={e => setParams(p => ({ ...p, ramp: e.target.value }))}
                    style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                    {Object.entries(COLOR_RAMPS).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: C.text2 }}>Min (blank = data)</label>
                  <input type="number" value={params.cmin ?? ''} onChange={e => setParams(p => ({ ...p, cmin: e.target.value }))}
                    placeholder="auto" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: C.text2 }}>Max (blank = data)</label>
                  <input type="number" value={params.cmax ?? ''} onChange={e => setParams(p => ({ ...p, cmax: e.target.value }))}
                    placeholder="auto" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
            </>
          )}

          {activeTool === 'lisa' && (
            <div>
              <label style={{ fontSize: 10, color: C.text2 }}>Method</label>
              <select value={params.hotspotMethod || 'lisa'} onChange={e => setParams(p => ({ ...p, hotspotMethod: e.target.value }))}
                style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                {HOTSPOT_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
          )}
          {needsK && (
            <div>
              <label style={{ fontSize: 10, color: C.text2 }}>Neighbors (k)</label>
              <input type="number" value={params.k || ''} onChange={e => setParams(p => ({ ...p, k: e.target.value }))}
                placeholder="8" style={{ width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          )}

          {error && <div style={{ fontSize: 10, color: C.red, padding: '4px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>{error}</div>}

          <button onClick={runTool} disabled={running}
            style={{ width: '100%', padding: '8px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, fontSize: 12, cursor: running ? 'wait' : 'pointer', opacity: running ? 0.6 : 1 }}>
            {running ? (progress > 0 ? `Running... ${Math.round(progress * 100)}%` : 'Running...') : `Run ${TOOLS.find(t => t.id === activeTool)?.label || ''}`}
          </button>
          {running && !BACKEND_TOOLS.includes(activeTool) && (
            <button onClick={() => abortRef.current && abortRef.current.abort()}
              style={{ width: '100%', padding: '5px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.text2, fontSize: 11, cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Log */}
      {log.length > 0 && (
        <div style={{ maxHeight: 100, overflow: 'auto', borderTop: `1px solid ${C.border}`, padding: '6px 10px' }}>
          {log.map((entry, i) => (
            <div key={i} style={{ fontSize: 10, color: entry.type === 'error' ? C.red : entry.type === 'success' ? C.emerald : entry.type === 'warning' ? C.amber : C.text3, marginBottom: 2 }}>
              <span style={{ color: C.text3 }}>{entry.time}</span> {entry.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
